export const removeEverythingButDigits = (str: string) => {
  if (str.length === 0) return str;
  const num = str.replace(/[^0-9]/g, "");
  return num;
};

export const removeEverythingButNumericValues = (str: string) => {
  if (str.length === 0) return str;
  // Note: inside a character class '?' was a literal, so question marks used
  // to survive sanitization. Keep '-' only as a leading sign.
  const num = str.replace(/[^-0-9.]/g, "");
  return num.startsWith("-")
    ? "-" + num.slice(1).replace(/-/g, "")
    : num.replace(/-/g, "");
};


/** Currency Y-axis: ₪10K, ₪10.5K; under 1000 stays full (e.g. ₪500). */
export function formatCurrencyAxisThousandsK(
  value: number | string,
  currencySymbol: string
): string {
  const n = Math.round(Number(value));
  if (!Number.isFinite(n)) return `${currencySymbol}0`;
  if (Math.abs(n) < 1000) {
    return `${currencySymbol}${formatNumber(String(n))}`;
  }
  const thousands = n / 1000;
  const body = Number.isInteger(thousands)
    ? String(thousands)
    : `${thousands.toFixed(1)}`.replace(/\.0$/, "");
  return `${currencySymbol}${body}K`;
}

export function nFormatter(num: number, digits: number) {
  const lookup = [
    { value: 1, symbol: "" },
    { value: 1e3, symbol: "k" },
    { value: 1e6, symbol: "M" },
    { value: 1e9, symbol: "G" },
    { value: 1e12, symbol: "T" },
    { value: 1e15, symbol: "P" },
    { value: 1e18, symbol: "E" },
  ];
  const regexp = /\.0+$|(?<=\.[0-9]*[1-9])0+$/;
  if (!Number.isFinite(num)) return "0";
  const sign = num < 0 ? "-" : "";
  const absNum = Math.abs(num);
  const item = lookup.findLast((tier) => absNum >= tier.value);
  return item
    ? sign +
        (absNum / item.value).toFixed(digits).replace(regexp, "").concat(item.symbol)
    : "0";
}

export const formatNumber = (
  str: string,
  additionalProperties?: {
    locale?: string;
    options?: {
      style?: string;
      currency?: string;
      maximumSignificantDigits?: number;
      minimumFractionDigits?: number;
      maximumFractionDigits?: number;
    };
  }
) => {
  if (!str || str.length === 0 || str === "-") return str;

  let completeNumericString = "";

  const { locale, options } = additionalProperties || {
    locale: undefined,
    options: {},
  };

  const cleanedInput = removeUnnecessaryMinuses(str);
  if (cleanedInput.length === 1) return cleanedInput;

  const num = Number(cleanedInput);
  const formattedNumber = num.toLocaleString(locale || "en", {
    ...options,
    maximumFractionDigits: options?.maximumFractionDigits || 2,
  });

  const numericPortionsWithoutDots = String(str).split(".");
  const splittedFormattedNumber = formattedNumber.split(".");
  if (numericPortionsWithoutDots.length > 1) {
    let everythingIsZero = true;
    for (let i = 0; i < numericPortionsWithoutDots[1].length; i++) {
      if (numericPortionsWithoutDots[1][i] !== "0") {
        everythingIsZero = false;
        break;
      }
    }

    if (everythingIsZero)
      completeNumericString +=
        splittedFormattedNumber[0] + "." + numericPortionsWithoutDots[1];
  } else if (str[str.length - 1] === ".")
    completeNumericString += splittedFormattedNumber[0] + ".";

  return completeNumericString.length > 0
    ? completeNumericString
    : formattedNumber;
};

const removeUnnecessaryMinuses = (str: string) => {
  if (String(str).includes("e-")) return str;

  const firstOccuranceOfMinusIndex = String(str).indexOf("-") + 1;

  const removedMinusesExceptForTheFirstOne =
    firstOccuranceOfMinusIndex === 0
      ? str
      : firstOccuranceOfMinusIndex === 1
      ? String(str).slice(0, firstOccuranceOfMinusIndex) +
        String(str).slice(firstOccuranceOfMinusIndex).replace(/-/g, "")
      : String(str).slice(0, firstOccuranceOfMinusIndex - 1);

  return removedMinusesExceptForTheFirstOne;
};

/** Minus before currency symbol (e.g. -₪1,018,124), for RTL tables where ₪-1,018,124 is confusing. */
export function formatCurrencyForDisplay(
  amount: number,
  currencySign: string
): string {
  const sign = currencySign || "₪";
  if (!Number.isFinite(amount)) return `${sign}0`;
  const absStr = formatNumber(String(Math.abs(amount)));
  if (amount < 0) return `-${sign}${absStr}`;
  return `${sign}${absStr}`;
}

/** Minus before the number (e.g. -4.45%); pair with dir="ltr" on cells in RTL for stable rendering. */
export function formatPercentForDisplay(value: number): string {
  if (!Number.isFinite(value)) return "0%";
  const absStr = formatNumber(String(Math.abs(value)));
  if (value < 0) return `-${absStr}%`;
  return `${absStr}%`;
}
